import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, Check, AlertTriangle } from 'lucide-react';
import { Card } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Textarea } from '../../../ui/textarea';
import {
  CALL_THEMES,
  describeAssignee,
  formatCallDate,
  groupCallsByMonth,
} from './callCalendarUtils';

const EMPTY_GUEST = { guest_name: '', guest_email: '', topic: '', notes: '' };
const GUEST_STATUS_LABELS = {
  placed: 'Placed. Invitation not sent yet.',
  invited: 'Invitation sent. Waiting for an answer.',
  confirmed: 'Confirmed.',
  declined: 'Declined.',
};
const GUEST_PREP_FIELDS = [
  ['story_title', 'Story title'],
  ['story_summary', 'Story'],
  ['main_takeaway', 'Main takeaway'],
  ['scripture', 'Scripture'],
  ['short_bio', 'Short bio'],
  ['phone', 'Phone'],
  ['tech_notes', 'Setup notes'],
];

const CallCalendarSection = ({ adminFetch, styles }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [guest, setGuest] = useState(EMPTY_GUEST);
  const [inviteEmail, setInviteEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState([]);
  const [feedback, setFeedback] = useState('');
  const [notesError, setNotesError] = useState('');

  const load = useCallback(async () => {
    if (!adminFetch) return;
    setLoading(true);
    setError('');
    try {
      const response = await adminFetch('/admin/call-calendar');
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.detail || `Failed to load the call calendar (${response.status})`);
      setData(payload);
    } catch (loadError) {
      setError(loadError.message || 'Failed to load the call calendar');
    } finally {
      setLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => { load(); }, [load]);

  const calls = useMemo(() => (Array.isArray(data?.calls) ? data.calls : []), [data]);
  const months = useMemo(() => groupCallsByMonth(calls), [calls]);
  const selected = calls.find((call) => call.event_id === selectedId) || null;
  const selectedBookingId = selected?.assignee?.type === 'guide' ? selected.assignee.booking_id : null;
  const coverage = data?.coverage || { next_calls: [], missing: [], all_set: true, count: 4 };
  const maxMinutes = data?.spiritual_guest_max_minutes || 30;

  const loadNotes = useCallback(async () => {
    if (!selectedBookingId || !adminFetch) return;
    setNotesError('');
    try {
      const response = await adminFetch(`/admin/story-notes/${selectedBookingId}`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.detail || `Failed to load notes (${response.status})`);
      setNotes(Array.isArray(payload?.notes) ? payload.notes : []);
    } catch (notesLoadError) {
      setNotesError(notesLoadError.message || 'Failed to load notes');
    }
  }, [selectedBookingId, adminFetch]);

  useEffect(() => {
    setNotes([]);
    setNotesError('');
    setFeedback('');
    loadNotes();
  }, [loadNotes]);

  const addFeedback = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const response = await adminFetch(`/admin/story-notes/${selectedBookingId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: feedback }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.detail || `Could not add feedback (${response.status})`);
      setNotes(Array.isArray(payload?.notes) ? payload.notes : []);
      setFeedback('');
    } catch (feedbackError) {
      setError(feedbackError.message || 'Could not add feedback');
    } finally {
      setBusy(false);
    }
  };

  const send = async (path, options, failureMessage) => {
    setBusy(true);
    setError('');
    try {
      const response = await adminFetch(path, options);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.detail || `${failureMessage} (${response.status})`);
      setData((current) => ({ ...current, ...payload }));
      return true;
    } catch (sendError) {
      setError(sendError.message || failureMessage);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const placeGuest = async (event) => {
    event.preventDefault();
    const ok = await send(
      '/admin/call-calendar/spiritual-guests',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_id: selected.event_id, ...guest }),
      },
      'Could not place the guest'
    );
    if (ok) setGuest(EMPTY_GUEST);
  };

  return (
    <Card className="p-6">
      <h3 className={`${styles.sectionTitle} mb-1 flex items-center gap-2`}>
        <CalendarDays className="h-5 w-5 text-aqua" />
        Call calendar
      </h3>
      <p className={`${styles.bodyText} mb-4`}>
        Who is speaking on each Sunday call, color coded by focus area. Guide Partners pick their own dates; you place
        Transformation guests here.
      </p>

      {error ? (
        <div className="mb-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {error}
          <Button type="button" variant="outline" size="sm" className="ml-3" onClick={load}>Try again</Button>
        </div>
      ) : null}

      {loading ? (
        <p className={styles.bodyText}>Loading the call calendar...</p>
      ) : (
        <>
          <div
            className={`mb-5 rounded-xl border px-4 py-3 text-sm ${
              coverage.all_set
                ? 'border-emerald-400 bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-200'
                : 'border-amber-400 bg-amber-50 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200'
            }`}
            data-testid="coverage-banner"
          >
            <p className="flex items-center gap-2 font-semibold">
              {coverage.all_set ? <Check className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              {coverage.all_set
                ? `Your next ${coverage.count} calls all have someone assigned.`
                : `${coverage.missing.length} of your next ${coverage.count} calls ${coverage.missing.length === 1 ? 'has' : 'have'} no one assigned yet.`}
            </p>
            <ul className="mt-2 space-y-1">
              {coverage.next_calls.map((call) => (
                <li key={call.event_id} className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="font-medium underline-offset-2 hover:underline"
                    onClick={() => setSelectedId(call.event_id)}
                  >
                    {formatCallDate(call.date, { weekday: 'long', month: 'long', day: 'numeric' })}
                  </button>
                  <span>· {CALL_THEMES[call.theme]?.label || (call.status === 'launch' ? 'Launch Call' : 'Call')}</span>
                  <span>· {describeAssignee(call)}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-3 flex flex-wrap gap-3 text-xs">
            {Object.entries(CALL_THEMES).map(([key, theme]) => (
              <span key={key} className="flex items-center gap-1.5">
                <span className={`h-3 w-3 rounded-full ${theme.dot}`} /> {theme.label}
              </span>
            ))}
            <span className="flex items-center gap-1.5">
              <Check className="h-3 w-3" /> assigned · dashed border = still open
            </span>
          </div>

          {months.length === 0 ? (
            <p className={styles.bodyText}>No calls are scheduled yet. Add events under Upcoming Events below.</p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {months.map((month) => (
                <div key={month.key} className={`${styles.panel} p-3`}>
                  <p className={`${styles.sectionTitle} mb-2 text-sm`}>{month.label}</p>
                  {month.calls.length === 0 ? (
                    <p className={`${styles.subtleText} text-xs`}>No calls</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {month.calls.map((call) => {
                        const theme = CALL_THEMES[call.theme];
                        const open = call.status === 'open';
                        return (
                          <button
                            key={call.event_id}
                            type="button"
                            onClick={() => setSelectedId(call.event_id)}
                            aria-label={`${formatCallDate(call.date, { month: 'long', day: 'numeric' })} ${theme?.label || 'call'}`}
                            className={`flex items-center gap-1 rounded-lg border px-2.5 py-1 text-sm ${
                              theme ? theme.chip : 'border-gray-400 bg-gray-400/20'
                            } ${open ? 'border-dashed' : ''} ${call.past ? 'opacity-50' : ''} ${
                              selectedId === call.event_id ? 'ring-2 ring-aqua' : ''
                            }`}
                          >
                            {formatCallDate(call.date, { month: 'short', day: 'numeric' })}
                            {call.status === 'assigned' ? <Check className="h-3 w-3" /> : null}
                            {call.status === 'launch' ? <span>★</span> : null}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {selected ? (
            <div className={`${styles.panel} mt-5 p-4`} data-testid="call-detail">
              <p className={`${styles.sectionTitle} text-base`}>
                {formatCallDate(selected.date, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                {' · '}
                {CALL_THEMES[selected.theme]?.label || (selected.status === 'launch' ? 'Launch Call' : 'Call')}
              </p>
              <p className={`${styles.bodyText} mt-1`}>{describeAssignee(selected)}</p>
              {selected.assignee?.email ? <p className={styles.subtleText}>{selected.assignee.email}</p> : null}
              {selected.assignee?.topic ? <p className={styles.subtleText}>Topic: {selected.assignee.topic}</p> : null}

              {selected.assignee?.type === 'guide' ? (
                <Button
                  type="button"
                  variant="outline"
                  className="mt-3"
                  disabled={busy}
                  onClick={() => send(
                    `/admin/call-calendar/guide-bookings/${selected.assignee.booking_id}`,
                    { method: 'DELETE' },
                    'Could not cancel the booking'
                  )}
                >
                  Cancel this booking
                </Button>
              ) : null}

              {selectedBookingId ? (
                <div className="mt-4" data-testid="story-notes">
                  <p className={`${styles.sectionTitle} text-sm`}>Private notes and feedback</p>
                  <p className={styles.subtleText}>Visible only to this Guide Partner and admins.</p>
                  {notesError ? (
                    <p className="mt-2 text-sm text-red-600" role="alert">
                      {notesError}
                      <Button type="button" variant="outline" size="sm" className="ml-3" onClick={loadNotes}>Try again</Button>
                    </p>
                  ) : notes.length === 0 ? (
                    <p className={`${styles.bodyText} mt-2`}>No notes yet.</p>
                  ) : (
                    <ul className="mt-2 space-y-2">
                      {notes.map((note) => (
                        <li key={note.note_id} className={`${styles.panel} p-3 text-sm`}>
                          <p className="font-semibold">
                            {note.author_type === 'admin' ? 'Admin feedback' : `Note from ${note.author_name}`}
                          </p>
                          <p className="mt-1 whitespace-pre-wrap">{note.body}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                  <form className="mt-3 space-y-2" onSubmit={addFeedback}>
                    <Textarea aria-label="Admin feedback" placeholder="Add feedback for this Guide Partner" value={feedback}
                      onChange={(e) => setFeedback(e.target.value)} />
                    <Button type="submit" disabled={busy || !feedback.trim()}>Add feedback</Button>
                  </form>
                </div>
              ) : null}

              {selected.assignee?.type === 'guest' ? (
                <div className="mt-3 space-y-3" data-testid="guest-invite">
                  <p className="text-sm font-semibold">{GUEST_STATUS_LABELS[selected.assignee.invite_status] || GUEST_STATUS_LABELS.placed}</p>
                  {selected.assignee.invite_status === 'declined' ? (
                    <p className="text-sm" role="status">
                      This guest cannot make it, so the call is open again.
                      {selected.assignee.decline_note ? ` Their note: ${selected.assignee.decline_note}` : ''} Remove them to place someone else.
                    </p>
                  ) : null}
                  {selected.assignee.invite_status === 'confirmed' ? (
                    <div className="space-y-1 text-sm" data-testid="guest-prep">
                      {GUEST_PREP_FIELDS.filter(([key]) => selected.assignee.prep?.[key]).map(([key, label]) => (
                        <div key={key}>
                          <p className="font-semibold">{label}</p>
                          <p className="whitespace-pre-wrap">{selected.assignee.prep[key]}</p>
                        </div>
                      ))}
                      {selected.assignee.recording_ok ? <p>Agreed to the call being recorded and shared.</p> : null}
                    </div>
                  ) : null}
                  {!selected.past && selected.assignee.invite_status !== 'confirmed' && selected.assignee.invite_status !== 'declined' ? (
                    <div className="space-y-2">
                      {!selected.assignee.email ? (
                        <Input aria-label="Guest email for invitation" placeholder="Guest email" value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)} />
                      ) : null}
                      <Button
                        type="button"
                        disabled={busy || (!selected.assignee.email && !inviteEmail.trim())}
                        onClick={async () => {
                          const ok = await send(
                            `/admin/call-calendar/spiritual-guests/${selected.event_id}/invite`,
                            {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ guest_email: inviteEmail.trim() || null }),
                            },
                            'Could not send the invitation'
                          );
                          if (ok) setInviteEmail('');
                        }}
                      >
                        {selected.assignee.invite_status === 'invited' ? 'Resend invitation' : 'Send invitation'}
                      </Button>
                    </div>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy}
                    onClick={() => send(
                      `/admin/call-calendar/spiritual-guests/${selected.event_id}`,
                      { method: 'DELETE' },
                      'Could not remove the guest'
                    )}
                  >
                    Remove this guest
                  </Button>
                </div>
              ) : null}

              {selected.status === 'open' && !selected.assignee && selected.theme === 'spiritual_growth' && !selected.past ? (
                <form className="mt-4 space-y-3" onSubmit={placeGuest}>
                  <p className={styles.subtleText}>
                    Place a Transformation guest. They tell their story for up to {maxMinutes} minutes.
                  </p>
                  <Input aria-label="Guest name" placeholder="Guest name" value={guest.guest_name}
                    onChange={(e) => setGuest({ ...guest, guest_name: e.target.value })} />
                  <Input aria-label="Guest email" placeholder="Guest email (optional)" value={guest.guest_email}
                    onChange={(e) => setGuest({ ...guest, guest_email: e.target.value })} />
                  <Input aria-label="Story topic" placeholder="Story topic (optional)" value={guest.topic}
                    onChange={(e) => setGuest({ ...guest, topic: e.target.value })} />
                  <Textarea aria-label="Notes" placeholder="Notes (optional)" value={guest.notes}
                    onChange={(e) => setGuest({ ...guest, notes: e.target.value })} />
                  <Button type="submit" disabled={busy || !guest.guest_name.trim()}>Place guest on this call</Button>
                </form>
              ) : null}

              {selected.status === 'open' && selected.theme !== 'spiritual_growth' ? (
                <p className={`${styles.subtleText} mt-3`}>
                  Waiting for a Guide Partner to pick this date.
                </p>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
};

export default CallCalendarSection;
